import { useCallback } from 'react'
import { DraftQuestionnaireEntry } from '../../../data/model/questionnaire'
import { useImportQuestionnaireV2 } from '../../../hooks/questionnaire/useImportQuestionnaireV2'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import {
  BuildTranslationImportOptions,
  buildTranslationImportEntries
} from '../lib/buildTranslationImportEntries'

export interface SaveViaImportResult {
  // Whether an `importQuestionnaireV2` call was issued (false when nothing was
  // importable — e.g. only text-card / audience fields, or empty titles).
  importCalled: boolean
  entryCount: number
  // Field keys written by the import call, or whose value is already stored.
  consumedKeys: Set<string>
  // Field keys that must fall back to a per-field save (see
  // buildTranslationImportEntries).
  residualKeys: Set<string>
}

// Saves Question/Matrix translations for a single language in ONE
// `importQuestionnaireV2` call (translation mode), replacing the per-entity
// `updateQuestionnaireEntryV2` / `updateResponseOptionV2` / `updateMatrixRowV2`
// loop. The mutation is atomic — a rejected promise means nothing was written,
// which callers surface to the user. See COR-137.
export const useSaveTranslationsViaImport = () => {
  const projectId = useProjectId()
  const waveId = useOptionalWaveId()
  const { importQuestionnaireV2 } = useImportQuestionnaireV2()

  const commit = useCallback(
    async (
      entries: ReadonlyArray<DraftQuestionnaireEntry>,
      languageCode: string,
      options?: BuildTranslationImportOptions
    ): Promise<SaveViaImportResult> => {
      const {
        entries: importEntries,
        consumedKeys,
        residualKeys
      } = buildTranslationImportEntries(entries, languageCode, options)

      if (importEntries.length > 0) {
        await importQuestionnaireV2({
          variables: {
            input: { projectId, waveId, languageCode, entries: importEntries }
          }
        })
      }

      return {
        importCalled: importEntries.length > 0,
        entryCount: importEntries.length,
        consumedKeys,
        residualKeys
      }
    },
    [importQuestionnaireV2, projectId, waveId]
  )

  return { commit }
}
